import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Linkedin, Twitter, Instagram, Facebook, Github, Link } from 'lucide-react';
import { VCardData, VCardSocialLinks } from '@/lib/vcard-utils';

interface VCardSocialMediaProps {
  data: VCardData;
  onChange: (data: Partial<VCardData>) => void;
}

export function VCardSocialMedia({ data, onChange }: VCardSocialMediaProps) {
  const updateSocial = (field: keyof VCardSocialLinks, value: string) => {
    onChange({
      social: { ...data.social, [field]: value },
    });
  };

  const addCustomLink = () => {
    if (data.social.custom.length >= 5) return;
    onChange({
      social: {
        ...data.social,
        custom: [...data.social.custom, { label: '', url: '' }],
      },
    });
  };

  const updateCustomLink = (index: number, updates: { label?: string; url?: string }) => {
    const newCustom = [...data.social.custom];
    newCustom[index] = { ...newCustom[index], ...updates };
    onChange({
      social: { ...data.social, custom: newCustom },
    });
  };

  const removeCustomLink = (index: number) => {
    onChange({
      social: {
        ...data.social,
        custom: data.social.custom.filter((_, i) => i !== index),
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* LinkedIn */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Linkedin className="h-4 w-4 text-[#0077B5]" />
          LinkedIn Profile
        </Label>
        <Input
          value={data.social.linkedin}
          onChange={(e) => updateSocial('linkedin', e.target.value)}
          placeholder="https://linkedin.com/in/yourprofile"
        />
      </div>

      {/* Twitter/X */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Twitter className="h-4 w-4 text-foreground" />
          Twitter/X Handle
        </Label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
          <Input
            value={data.social.twitter}
            onChange={(e) => updateSocial('twitter', e.target.value.replace('@', ''))}
            placeholder="username"
            className="pl-8"
          />
        </div>
      </div>

      {/* Instagram */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Instagram className="h-4 w-4 text-[#E4405F]" />
          Instagram Username
        </Label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
          <Input
            value={data.social.instagram}
            onChange={(e) => updateSocial('instagram', e.target.value.replace('@', ''))}
            placeholder="username"
            className="pl-8"
          />
        </div>
      </div>

      {/* Facebook */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Facebook className="h-4 w-4 text-[#1877F2]" />
          Facebook Profile
        </Label>
        <Input
          value={data.social.facebook}
          onChange={(e) => updateSocial('facebook', e.target.value)}
          placeholder="https://facebook.com/yourprofile"
        />
      </div>

      {/* GitHub */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Github className="h-4 w-4" />
          GitHub Username
        </Label>
        <Input
          value={data.social.github}
          onChange={(e) => updateSocial('github', e.target.value)}
          placeholder="username"
        />
      </div>

      {/* Custom Links */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2">
            <Link className="h-4 w-4" />
            Custom Social Links
          </Label>
          <span className="text-xs text-muted-foreground">{data.social.custom.length}/5</span>
        </div>

        {data.social.custom.map((link, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              value={link.label}
              onChange={(e) => updateCustomLink(index, { label: e.target.value })}
              placeholder="Label (e.g., TikTok)"
              className="w-[120px]"
            />
            <Input
              value={link.url}
              onChange={(e) => updateCustomLink(index, { url: e.target.value })}
              placeholder="https://..."
              className="flex-1"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeCustomLink(index)}
            >
              <Trash2 className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        ))}

        {data.social.custom.length < 5 && (
          <Button type="button" variant="outline" size="sm" onClick={addCustomLink} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Custom Link
          </Button>
        )}
      </div>
    </div>
  );
}
